import { DialogContentText } from '@mui/material';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import type { Or2DialogProps } from '@/components/feedback/Or2Dialog';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { Or2Button } from '@/components/button/Or2Button';
import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import {
  addNewFileToCache,
  removeFileFromCache,
} from '@/modules/files/utils/fileUpdateFunctions';

import { MOVE_FILE_MUTATION } from '../../content/file-row/options-component/option-items/MoveFileMenuItem';

const FILE_MANAGER_CHANGE_OWNER_FILE_FRAGMENT = graphql(`
  fragment fileManagerChangeOwnerFileFields on File {
    ...fileFields
  }
`);

export interface ChangeOwnerDialogProps extends Omit<Or2DialogProps, 'title'> {
  file: DocumentType<typeof FILE_MANAGER_CHANGE_OWNER_FILE_FRAGMENT>;
  parentId: string | null | undefined;
  ownerId: string | null | undefined;
  rootOwnerId: string;
  ownerName: string;
  isChangingParent: boolean;
  isFolder: boolean;
  isMovingToRoot: boolean;
  onClose: () => void;
}

export const FileManagerChangeOwnerConfirmDialog = ({
  file,
  parentId,
  ownerId,
  rootOwnerId,
  ownerName,
  isChangingParent,
  isFolder,
  isMovingToRoot,
  onClose,
  ...other
}: ChangeOwnerDialogProps) => {
  const [changeOwner, { loading }] = useMutation(MOVE_FILE_MUTATION, {
    onCompleted: onClose,
    update(cache, { data }) {
      const newFile = data?.moveFile?.file;

      removeFileFromCache(cache, file, rootOwnerId);
      addNewFileToCache(cache, newFile, rootOwnerId);
    },
  });

  return (
    <>
      <Or2Dialog
        hideCloseButton
        small
        aria-describedby="change-owner-dialog-description"
        title={`Are you sure you want to 
        ${isChangingParent ? 'move' : 'change the visibility of'} ${file.filename}?`}
        loading={loading}
        actions={
          <>
            <Or2Button onClick={onClose}>No</Or2Button>
            <Or2Button
              color="secondary"
              onClick={async () => {
                await changeOwner({
                  variables: {
                    data: {
                      canMoveOwner: true,
                      file: file._id,
                      owner: ownerId ?? null,
                      parent: parentId ?? null,
                    },
                  },
                });
              }}
            >
              Yes
            </Or2Button>
          </>
        }
        onClose={onClose}
        {...other}
      >
        <DialogContentText id="change-owner-dialog-description">
          After doing this{' '}
          <strong>
            {isMovingToRoot
              ? `everyone in ${ownerName}`
              : `only members of ${ownerName}`}
          </strong>{' '}
          will be able to see{' '}
          {isFolder ? 'the folder and the files within it' : 'it'}.
        </DialogContentText>
      </Or2Dialog>
    </>
  );
};
